import React, { useEffect, useId, useRef, useState } from "react";
import { C, radius, shadow, text, statusLabel, STATUS_COLORS } from "../theme";
import { evaluatePassword } from "../lib/passwordPolicy";

// Status-family glyphs so pill meaning never rests on color alone (WCAG 1.4.1).
// Families follow the STATUS_COLORS text token; DRAFT/CANCELLED share gray and
// get their own glyphs. aria-hidden — screen readers read the label itself.
const FAMILY_GLYPH = {
  [C.success]: "✓",  // approved / completed / paid
  [C.warning]: "⏳", // pending / needs attention (matches the approval trail)
  [C.danger]: "✕",   // rejected / unpaid / over budget
  [C.primary]: "→",  // in flight (issued, in evaluation)
  [C.purple]: "◆",   // cross-system (Xero, virement, consolidated)
};
const STATUS_GLYPH = { DRAFT: "○", CANCELLED: "—", RETURNED: "↩" };

// Keyboard-operable click target for table rows and list tiles (WCAG 2.1.1):
// spread onto an element with an onClick to make Tab reach it and Enter/Space
// activate it. The currentTarget guard keeps nested buttons/inputs unaffected.
export function rowAction(onOpen) {
  return {
    tabIndex: 0,
    onClick: onOpen,
    onKeyDown: (e) => {
      if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
        e.preventDefault();
        onOpen();
      }
    },
  };
}

// Persistent, dismissible feedback banner (UX-08/UX-75) — the pattern the
// Approvals page introduced in #45, shared so every page reports async
// results the same way instead of window.alert(). notice is
// { kind: "success" | "error", text } or null; role=alert/status makes the
// result audible to screen readers without moving focus.
export function Notice({ notice, onDismiss, style }) {
  if (!notice) return null;
  const isError = notice.kind === "error";
  return (
    <div role={isError ? "alert" : "status"} style={{
      display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px", marginBottom: 16,
      borderRadius: 8, fontSize: text.sm, fontWeight: 600, whiteSpace: "pre-line",
      background: isError ? C.dangerBg : C.successBg,
      color: isError ? C.danger : C.success,
      border: `1px solid ${isError ? C.dangerLight : C.successLight}`,
      ...style,
    }}>
      <span style={{ flex: 1 }}>{notice.text}</span>
      {onDismiss && (
        <button onClick={onDismiss} aria-label="Dismiss message" style={{
          background: "none", border: "none", cursor: "pointer", color: "inherit",
          fontSize: text.md, fontWeight: 700, lineHeight: 1, padding: 0,
        }}>×</button>
      )}
    </div>
  );
}

export function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || { bg: C.gray100, text: C.gray700 };
  const glyph = STATUS_GLYPH[status] || FAMILY_GLYPH[c.text];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: radius.pill,
        fontSize: text.xs,
        fontWeight: 700,
        background: c.bg,
        color: c.text,
        whiteSpace: "nowrap",
      }}
    >
      {glyph && <span aria-hidden="true" style={{ marginRight: 5 }}>{glyph}</span>}
      {statusLabel(status)}
    </span>
  );
}

export function Card({ children, style }) {
  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${C.gray200}`,
        borderRadius: radius.card,
        boxShadow: shadow,
        padding: 20,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// Stage-kind vocabulary, shared by the approval card and every trail (UX-28):
// a SUPPORT stage *endorses* (non-binding); the final stage is the binding
// approval. One phrasing everywhere, so "Support", "Endorse" and "Approver"
// stop reading as three unrelated concepts.
export const stageKindLabel = (stepType) =>
  stepType === "SUPPORT" ? "Support stage · endorses (non-binding)" : "Approver stage · binding approval";

// Shared approval trail for document detail pages. Explains the two routing
// behaviors users otherwise can't see (UX-14): the automatically injected
// Team Lead support stage, and invoice stages that carry no business unit.
export function ApprovalTrail({ approvals, docType, style }) {
  if (!approvals || approvals.length === 0) return null;
  return (
    <Card style={{ marginBottom: 16, ...style }}>
      <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 10 }}>Approval Trail</div>
      {approvals.map((a) => {
        const injectedTL = a.stepType === "SUPPORT" && a.approverRole === "Team Lead" && a.layer === 1;
        const anyBu = docType === "INVOICE" && a.bu == null;
        return (
          <div key={a.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 0", borderBottom: `1px solid ${C.gray100}` }}>
            <div aria-hidden="true" style={{ width: 28, height: 28, borderRadius: "50%", background: a.status === "APPROVED" ? C.successBg : a.status === "REJECTED" ? C.dangerBg : C.warningBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {a.status === "APPROVED" ? "✓" : a.status === "REJECTED" ? "✗" : "⏳"}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: text.md, fontWeight: 600 }}>
                Layer {a.layer}: {a.approverRole}
                {a.bu && <span style={{ marginLeft: 6, fontSize: text.xs, color: C.gray500 }}>[{a.bu}]</span>}
                {a.stepType && <span style={{ marginLeft: 8, fontSize: text.xs, fontWeight: 600, color: C.gray500 }}>{stageKindLabel(a.stepType)}</span>}
              </div>
              {injectedTL && (
                <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 2 }}>
                  Added automatically — submissions by anyone who is not a Team Lead route through a Team Lead endorsement first.
                </div>
              )}
              {anyBu && (
                <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 2 }}>
                  Invoice approval stages are not tied to one business unit — any holder of this layer may act.
                </div>
              )}
              {a.actions?.map((act, ai) => (
                <div key={ai} style={{ fontSize: text.xs, color: C.gray500, marginTop: 2 }}>
                  {act.action} by {act.user?.displayName} • {new Date(act.actionDate).toLocaleString()} • COI: {act.coiDeclared ? "✓" : "—"}
                  {act.comments && (
                    <div style={{ fontSize: text.sm, color: act.action === "REJECTED" ? C.danger : C.gray700, background: act.action === "REJECTED" ? C.dangerBg : C.gray50, borderRadius: 6, padding: "6px 10px", marginTop: 4 }}>
                      {act.action === "REJECTED" ? "Reason: " : ""}{act.comments}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <StatusPill status={a.status} />
          </div>
        );
      })}
    </Card>
  );
}

// Banner for a document an approver returned (UX-06): names the returner and
// the reason so the requestor learns what to change without digging through
// the trail.
export function ReturnedBanner({ status, approvals, style }) {
  if (status !== "RETURNED") return null;
  const rejects = (approvals || [])
    .flatMap((a) => a.actions || [])
    .filter((x) => x.action === "REJECTED")
    .sort((x, y) => new Date(y.actionDate) - new Date(x.actionDate));
  const last = rejects[0];
  return (
    <Card role="status" style={{ marginBottom: 16, background: C.dangerBg, border: `1px solid ${C.dangerLight}` , ...style }}>
      <div style={{ fontSize: text.md, fontWeight: 700, color: C.danger, marginBottom: 4 }}>↩ Returned to requestor</div>
      <div style={{ fontSize: text.sm, color: C.gray700, lineHeight: 1.5 }}>
        {last ? (
          <>Returned by <b>{last.user?.displayName || "an approver"}</b> on {new Date(last.actionDate).toLocaleString()}{last.comments ? <> — reason: <b>{last.comments}</b></> : "."}</>
        ) : (
          <>An approver returned this document.</>
        )}{" "}
        Amend it and submit it again.
      </div>
    </Card>
  );
}

// Inline cross-document link (UX-43/49): a real, keyboard-operable control
// styled as a link, for jumping to a related record via onOpenDoc.
export function DocLink({ onClick, children, style }) {
  return (
    <button type="button" onClick={onClick} style={{
      background: "none", border: "none", padding: 0, color: C.primary, fontWeight: 600,
      cursor: "pointer", fontSize: "inherit", fontFamily: "inherit", textDecoration: "underline",
      ...style,
    }}>{children}</button>
  );
}

// Accessible modal dialog (UX-76): role="dialog" + aria-modal, focus moves
// into the dialog on open and back to the opener on close, Tab is trapped
// inside, and Escape closes. Every app modal (confirms, admin forms, the
// temp-password overlay) builds on this instead of a positioned <div>.
const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
export function Dialog({ open, title, onClose, children, width = 460, initialFocus = "box" }) {
  const boxRef = useRef(null);
  const openerRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    const t = setTimeout(() => {
      const box = boxRef.current;
      if (!box) return;
      const first = initialFocus === "first" ? box.querySelector(FOCUSABLE) : null;
      (first || box).focus();
    }, 0);
    return () => {
      clearTimeout(t);
      const opener = openerRef.current;
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, [open, initialFocus]);
  if (!open) return null;
  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.stopPropagation(); onClose(); return; }
    if (e.key !== "Tab" || !boxRef.current) return;
    const items = [...boxRef.current.querySelectorAll(FOCUSABLE)].filter((el) => !el.disabled);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === boxRef.current)) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  };
  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{
      position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={boxRef} onKeyDown={onKeyDown}
        style={{ background: C.white, borderRadius: radius.card, boxShadow: "0 20px 50px rgba(0,0,0,0.25)", width: "100%", maxWidth: width, padding: 20, outline: "none", maxHeight: "85vh", overflowY: "auto" }}>
        {title && <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 8 }}>{title}</div>}
        {children}
      </div>
    </div>
  );
}

// Modal confirmation (UX-12/40/41/52/79): replaces the window.confirm/window.prompt
// call sites so commitments state their consequence before committing, and
// reasons are captured in a real field. Built on Dialog, so it is keyboard-
// operable end to end (focus trap, Escape cancels, focus returns to the opener).
export function ConfirmDialog({ open, title, children, confirmLabel = "Confirm", variant = "primary", requireReason = false, reasonLabel = "Reason", onConfirm, onCancel, busy }) {
  const [reason, setReason] = useState("");
  const reasonId = useId();
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!open) return null;
  const canConfirm = !requireReason || reason.trim().length > 0;
  return (
    <Dialog open={open} title={title} onClose={onCancel} initialFocus={requireReason ? "first" : "box"}>
      <div style={{ fontSize: text.md, color: C.gray700, lineHeight: 1.5, marginBottom: 14, whiteSpace: "pre-line" }}>{children}</div>
      {requireReason && (
        <div style={{ marginBottom: 14 }}>
          <label htmlFor={reasonId} style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }}>{reasonLabel} *</label>
          <textarea id={reasonId} value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.gray300}`, borderRadius: radius.control, fontSize: text.md, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical" }} />
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
        <Btn variant={variant} disabled={busy || !canConfirm} onClick={() => onConfirm(requireReason ? reason.trim() : undefined)}>{confirmLabel}</Btn>
      </div>
    </Dialog>
  );
}

const BTN_BG = {
  primary: C.primary,
  success: C.success,
  danger: C.danger,
  purple: C.purple,
  outline: "transparent",
  dangerOutline: "transparent",
};

// Destructive-action hierarchy (UX-79): row-level destructive controls use the
// quiet "dangerOutline" variant — red text, not a solid red block — so they
// stop dominating the page. The solid "danger" button lives inside the
// ConfirmDialog that states the consequence, where the commitment is made.
export function Btn({ children, variant = "primary", disabled, style, ...rest }) {
  const [hover, setHover] = useState(false);
  const outline = variant === "outline" || variant === "dangerOutline";
  const dangerOutline = variant === "dangerOutline";
  const bg = BTN_BG[variant] || C.primary;
  return (
    <button
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: disabled ? C.gray200 : outline && hover ? (dangerOutline ? C.dangerBg : C.gray100) : bg,
        color: disabled ? C.gray400 : dangerOutline ? C.danger : outline ? C.gray700 : C.white,
        border: outline ? `1px solid ${dangerOutline ? C.dangerLight : C.gray300}` : "none",
        borderRadius: radius.control,
        padding: "8px 18px",
        fontSize: text.md,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "inherit",
        filter: hover && !disabled && !outline ? "brightness(0.9)" : "none",
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

const labelStyle = {
  fontSize: text.sm,
  fontWeight: 600,
  color: C.gray700,
  display: "block",
  marginBottom: 4,
};

const controlStyle = {
  width: "100%",
  padding: "8px 12px",
  border: `1px solid ${C.gray300}`,
  borderRadius: radius.control,
  fontSize: text.md,
  fontFamily: "inherit",
  boxSizing: "border-box",
};

export function Input({ label, id, style, ...props }) {
  // Associate the label with the control (screen readers, click-to-focus).
  const autoId = useId();
  const inputId = id || (label ? autoId : undefined);
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label htmlFor={inputId} style={labelStyle}>{label}</label>}
      <input id={inputId} {...props} style={{ ...controlStyle, ...style }} />
    </div>
  );
}

export function Select({ label, id, options, children, style, ...props }) {
  const autoId = useId();
  const selectId = id || (label ? autoId : undefined);
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label htmlFor={selectId} style={labelStyle}>{label}</label>}
      <select id={selectId} {...props} style={{ ...controlStyle, background: C.white, ...style }}>
        {options ? (
          <>
            <option value="">-- Select --</option>
            {options.map((o, i) => (
              <option key={i} value={o.value}>{o.label}</option>
            ))}
          </>
        ) : (
          children
        )}
      </select>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

export function MetricCard({ label, value, trend, accent }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 150,
        background: C.white,
        border: `1px solid ${C.gray200}`,
        borderRadius: radius.card,
        boxShadow: shadow,
        padding: 24,
      }}
    >
      <div style={{ fontSize: text.sm, color: C.gray600, marginBottom: 6 }}>{label}</div>
      <div
        style={{
          fontSize: text.metric,
          fontWeight: 600,
          letterSpacing: "-0.02em",
          color: accent || C.gray1100,
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {trend && (
        <div style={{ fontSize: text.sm, color: C.gray600, marginTop: 6 }}>{trend}</div>
      )}
    </div>
  );
}

// `empty` (UX-21): what an empty list means and how it gets filled — a
// sentence, not just "No records".
export function DataTable({ columns, rows, rowKey, empty }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                style={{
                  padding: "10px 12px",
                  textAlign: "left",
                  borderBottom: `1px solid ${C.gray200}`,
                  color: C.gray600,
                  fontWeight: 700,
                  fontSize: text.sm,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  whiteSpace: "nowrap",
                }}
              >
                {/* Action/open columns have no visible label; give screen readers
                    one so the header is never empty (axe empty-table-header). */}
                {col.label || <span className="sr-only">{col.srLabel || "Actions"}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ padding: "24px 12px", textAlign: "center", color: C.gray500 }}
              >
                {empty || "No records"}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                style={{ borderBottom: `1px solid ${C.gray100}`, height: 48 }}
              >
                {columns.map((col) => (
                  <td key={col.key} style={{ padding: "10px 12px" }}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const SCORE_COLORS = [C.danger, C.danger, C.warning, C.successLight, C.success];
// Label text needs AA contrast on white; successLight (score 3) is a meter-bar
// color only, so the label swaps it for the full success token.
const SCORE_LABEL_COLORS = [C.danger, C.danger, C.warning, C.success, C.success];

function EyeIcon({ off }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
  );
}

function Req({ ok, children }) {
  return (
    <li style={{ display: "flex", alignItems: "center", gap: 6, color: ok ? C.success : C.gray500 }}>
      <span aria-hidden="true">{ok ? "✓" : "○"}</span>
      <span>{children}</span>
    </li>
  );
}

function SubReq({ ok, children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: ok ? C.success : C.gray500 }}>
      <span aria-hidden="true">{ok ? "✓" : "○"}</span>{children}
    </span>
  );
}

function StrengthMeter({ ev }) {
  const color = SCORE_COLORS[ev.score];
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 4 }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i < ev.score ? color : C.gray200 }} />
        ))}
      </div>
      <div style={{ fontSize: text.sm, color: SCORE_LABEL_COLORS[ev.score], marginTop: 4 }}>{ev.length === 0 ? "" : ev.label}</div>
      <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", fontSize: text.sm, display: "flex", flexDirection: "column", gap: 4 }}>
        <Req ok={ev.checks.minLength}>At least 15 characters</Req>
        <Req ok={ev.checks.classCount}>At least 3 of these 4 character types:</Req>
        <li style={{ display: "flex", flexWrap: "wrap", gap: 12, paddingLeft: 22 }}>
          <SubReq ok={ev.classes.lower}>lowercase</SubReq>
          <SubReq ok={ev.classes.upper}>uppercase</SubReq>
          <SubReq ok={ev.classes.digit}>number</SubReq>
          <SubReq ok={ev.classes.symbol}>symbol</SubReq>
        </li>
      </ul>
    </div>
  );
}

export function PasswordInput({ showStrength = false, style, value = "", ...rest }) {
  const [visible, setVisible] = useState(false);
  const ev = showStrength ? evaluatePassword(value) : null;
  return (
    <div style={{ width: "100%" }}>
      <div style={{ position: "relative" }}>
        <input
          {...rest}
          value={value}
          type={visible ? "text" : "password"}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "10px 12px",
            border: `1px solid ${C.gray300}`,
            borderRadius: radius.input,
            ...style,
            paddingRight: 40,
          }}
        />
        <button
          type="button"
          aria-label={visible ? "Hide password" : "Show password"}
          onClick={() => setVisible((v) => !v)}
          style={{
            position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer", padding: 4,
            color: C.gray500, display: "flex",
          }}
        >
          <EyeIcon off={visible} />
        </button>
      </div>
      {showStrength && <StrengthMeter ev={ev} />}
    </div>
  );
}
